import { useNavigate } from 'react-router-dom';
import { useShop, useNotificationPrefs } from '@/state';
import type { NotificationPreferences } from '@alkor/types';
import { pluralize } from '@alkor/utils';
import { IconHeart, IconWarn } from '@/components/icons';
import { EmptyState, ProductTile, Notice, TileSkeleton } from '@/components/ui';

/* ── Избранное ──────────────────────────────────────────────────────────── */

export function FavoritesScreen() {
  const navigate = useNavigate();
  const { favorites, inCart, addToCart, subscribeStock, busy } = useShop();

  if (favorites.length === 0) {
    return (
      <EmptyState
        icon={<IconHeart size={24} />}
        title="Здесь пока пусто"
        text="Отмечайте товары сердечком — они сохранятся и помогут подбирать уведомления по вашим интересам, а не рассылать всё подряд."
        action={<button type="button" className="secbtn" onClick={() => navigate('/')}>Перейти в каталог</button>}
      />
    );
  }

  return (
    <>
      <div className="screen-title">Избранное</div>
      <div className="screen-sub">{pluralize(favorites.length, 'товар', 'товара', 'товаров')}</div>
      <div className="block" style={{ marginTop: 16 }}>
        <div className="plist">
          {favorites.map((p) => (
            <ProductTile
              key={p.id}
              product={p}
              inCart={inCart(p.id)}
              busy={busy}
              onAdd={(x) => void addToCart(x)}
              onNotify={(x) => void subscribeStock(x)}
            />
          ))}
        </div>
      </div>
      <div className="pad" />
    </>
  );
}

/* ── Настройки уведомлений ──────────────────────────────────────────────── */

const TOPICS: { key: keyof NotificationPreferences; title: string; sub: string }[] = [
  { key: 'newArrivals',   title: 'Новые поступления',      sub: 'Новинки в категориях, которые вы смотрели' },
  { key: 'priceDrop',     title: 'Снижение цены',          sub: 'Только по товарам, за которыми вы следите' },
  { key: 'stockBack',     title: 'Товар снова в наличии',  sub: 'По вашим подпискам на поступление' },
  { key: 'cartReminders', title: 'Напоминания о корзине',  sub: 'Не более трёх за одну корзину' },
  { key: 'shopNews',      title: 'Новости магазина',       sub: 'Редко, только существенное' },
];

export function NotificationsScreen() {
  const { prefs, isLoading, update } = useNotificationPrefs();

  if (isLoading || !prefs) return <div className="block" style={{ marginTop: 20 }}><TileSkeleton count={3} /></div>;

  return (
    <>
      <div className="screen-title">Уведомления</div>
      <div className="screen-sub">Не более одного рекламного сообщения в сутки</div>

      <div style={{ marginTop: 18, borderTop: '1px solid var(--c-border-soft)' }}>
        {TOPICS.map((t) => {
          const on = prefs[t.key] && prefs.marketingEnabled;
          return (
            <div className="toggle-row" key={t.key}>
              <div>
                {t.title}
                <small>{t.sub}</small>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={t.title}
                className={`sw-toggle${on ? ' sw-toggle--on' : ''}`}
                disabled={!prefs.marketingEnabled}
                onClick={() => update({ [t.key]: !prefs[t.key] })}
              />
            </div>
          );
        })}
      </div>

      <div className="block" style={{ marginTop: 22 }}>
        <button
          type="button"
          className="secbtn"
          style={prefs.marketingEnabled ? undefined : { borderColor: 'var(--c-accent-line)', color: 'var(--c-accent-hover)' }}
          onClick={() => update({ marketingEnabled: !prefs.marketingEnabled })}
        >
          {prefs.marketingEnabled
            ? 'Отключить все рекламные уведомления'
            : 'Рекламные уведомления отключены — включить'}
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        <Notice>
          Сообщения о заказах, статусах и запрошенных вами поступлениях приходят всегда — это не реклама.
        </Notice>
      </div>

      <div className="pad" />
    </>
  );
}

/* ── 404 ────────────────────────────────────────────────────────────────── */

export function NotFoundScreen() {
  const navigate = useNavigate();
  return (
    <EmptyState
      icon={<IconWarn size={24} />}
      title="Страница не найдена"
      text="Возможно, ссылка устарела или товар снят с продажи."
      action={<button type="button" className="secbtn" onClick={() => navigate('/')}>На главную</button>}
    />
  );
}
